import {
  Box,
  BoxProps,
  TableCellProps as ChakraTableCellProps,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { useStableCallback } from '@main/shared/browser';
import { Promisable } from '@main/shared/types';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import isEqual from 'fast-deep-equal';
import { SyntheticEvent, useContext, useDeferredValue, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DragHandleIcon } from '../../icons/drag-handle';
import { whenNotSelecting } from '../../utils/selection';
import { ActionButtonProps } from '../floating-action-buttons/action-buttons';
import { TableAction, TableActionHandler } from './table-action';
import { selectionColumnId } from './table-column-ids';
import { TableContext } from './table-context';
import { useCommonPinningStyles } from './use-common-pinning-styles';

export type TableCellColumnMeta<TData> = Omit<ChakraTableCellProps, 'onClick'> & {
  onClick?: (cell: Cell<TData, unknown>) => Promisable<void | TableAction>;
  forceEditViaFloatingButton?: boolean;
  /** Tooltip and aria-label for the pencil edit trigger in editable cells. */
  editTriggerTooltip?: string;
  actions?: (row: Row<TData>) => ActionButtonProps['buttons'];
};

export interface TableCellProps<TData> {
  cell: Cell<TData, unknown>;
  boxProps?: BoxProps;
  isReorderable?: boolean;
  actionHandler: TableActionHandler<TData>;
}

export function TableCell<TData>({
  cell,
  boxProps,
  isReorderable,
  actionHandler,
}: TableCellProps<TData>) {
  const { t } = useTranslation('ui');
  const { isOverflowing } = useContext(TableContext);

  const columnDef = cell.column.columnDef;
  const table = cell.getContext().table;
  const meta = columnDef.meta;
  const {
    actions,
    forceEditViaFloatingButton,
    editTriggerTooltip: _editTriggerTooltip,
    ...cellMeta
  } = meta?.cell ?? {};
  const cursor = cellMeta?.onClick && 'pointer';
  const action = meta?.action;
  const actionTrigger = action?.triggerOn ?? 'onClick';
  const isFirstColumn = cell.column.getIsFirstColumn();
  const colSpan =
    cell.column.getIsFirstColumn('left') || cell.column.getIsLastColumn('right') ? 2 : 1;
  const reorderTooltipDisclosureRaw = useDisclosure();
  const onCloseTooltip = useStableCallback(reorderTooltipDisclosureRaw.onClose);
  const onOpenTooltip = useStableCallback(reorderTooltipDisclosureRaw.onOpen);
  const reorderTooltipDisclosure = useMemo(
    () => ({
      isOpen: reorderTooltipDisclosureRaw.isOpen,
      onClose: onCloseTooltip,
      onOpen: onOpenTooltip,
    }),
    [onCloseTooltip, onOpenTooltip, reorderTooltipDisclosureRaw.isOpen],
  );

  const onClick = useMemo(
    () => cellMeta?.onClick && whenNotSelecting(cellMeta.onClick),
    [cellMeta?.onClick],
  );

  const handleAction = useStableCallback((event: SyntheticEvent) => {
    event.stopPropagation();

    if (!action) {
      return;
    }

    const resultAction = action.actionFactory
      ? action.actionFactory(cell.row, cell, event)
      : action;

    resultAction && actionHandler(resultAction, cell.row, cell);
  });

  const handleClick = useStableCallback(async (event: SyntheticEvent) => {
    event.stopPropagation();
    if (!onClick) {
      if (actionTrigger === 'onClick') {
        handleAction(event);
      }

      return;
    }

    const resultAction = await onClick(cell);

    if (resultAction) {
      actionHandler(resultAction, cell.row, cell);
    } else if (actionTrigger === 'onClick') {
      handleAction(event);
    }
  });

  const isEditableCell = Boolean(columnDef.meta?.editable?.(cell.row.original, cell.getContext()));

  const handlers = useMemo(() => {
    const baseOnClick = onClick ? handleClick : action ? handleAction : undefined;

    // Row onRowClick listens on the row box; cell-level handlers must not bubble there.
    // When there is no cell onClick/action, editable columns still need a capture on the cell
    // (e.g. EditablePopover on the inner surface) so the row does not open.
    const onClickHandler = baseOnClick
      ? (event: SyntheticEvent) => {
          event.stopPropagation();
          void baseOnClick(event);
        }
      : isEditableCell && !forceEditViaFloatingButton
        ? (event: SyntheticEvent) => {
            event.stopPropagation();
          }
        : undefined;

    return {
      [actionTrigger]: action ? handleAction : undefined,
      onClick: onClickHandler,
    };
  }, [
    action,
    actionTrigger,
    forceEditViaFloatingButton,
    handleAction,
    handleClick,
    isEditableCell,
    onClick,
  ]);

  const { attributes, listeners, isDragging } = useSortable({ id: cell.row.id });

  const dragHandleStyles = useMemo(
    () => ({
      position: 'absolute',
      display: 'flex',
      top: 0,
      left: 0,
      bottom: 0,
      w: 5,
      paddingLeft: 1,
      alignItems: 'center',
      justifyContent: 'center',
      color: 'gray.500',
      cursor: 'grab',
      userSelect: 'none',
      zIndex: 1,
      opacity: 0,
      transition: 'opacity 0.2s ease-out',
      ':hover': {
        opacity: 1,
      },
    }),
    [],
  );

  useEffect(() => {
    if (isDragging) {
      onCloseTooltip();
    }
  }, [isDragging, onCloseTooltip]);
  const pinnedStyles = useCommonPinningStyles(cell.column, isOverflowing);

  const showEditableStyles =
    isEditableCell &&
    !columnDef.meta?.cell?.onClick &&
    !columnDef.meta?.cell?.forceEditViaFloatingButton &&
    (columnDef.meta?.editableStyles ?? true);

  const isSelectionCell = cell.column.id === selectionColumnId;
  const isSelected = isSelectionCell ? cell.row.getIsSelected() : undefined;

  const cellValue = cell.getValue();

  const rowValue = cell.row.original;
  const prevRowValue = useDeferredValue(rowValue);
  const hasRowValuesChanged = isEqual(rowValue, prevRowValue);
  const sortedRows = table.getSortedRowModel().rows;

  return useMemo(
    () => (
      <Box
        aria-label={meta?.name}
        cursor={cursor}
        /* The table sets the size its cells read at, once, so every column
         * agrees without each one naming a step. It sits before the column's
         * own meta so a column that means something different by its type,
         * such as the smaller step dates use, still wins. */
        fontSize="sm"
        {...cellMeta}
        {...handlers}
        role="cell"
        className={`why-table--cell ${showEditableStyles ? 'why-table--cell--editable' : ''}`}
        gridColumnEnd={`span ${colSpan}`}
        {...boxProps}
        style={pinnedStyles}
      >
        {isReorderable && isFirstColumn && (
          <Tooltip
            label={t('table.rows.dragHandleLabel')}
            openDelay={250}
            {...reorderTooltipDisclosure}
          >
            <Box
              {...attributes}
              {...listeners}
              __css={dragHandleStyles}
              aria-label={t('table.rows.dragHandleLabel')}
            >
              <DragHandleIcon color="gray.500" />
            </Box>
          </Tooltip>
        )}
        <Box w="full" display="flex">
          {flexRender(columnDef.cell, cell.getContext())}
        </Box>
      </Box>
    ),
    // Optimise re-rendering of the cell
    // Explicitly listing additional dependencies need to trigger a re-render:
    // - cell.id
    // - cellValue
    // - hasRowValuesChanged
    // - isSelected
    // - sortedRows
    // Purposely omitting the following dependencies which are different on every render and should not trigger a re-render:
    // - cell
    // - columnDef.cell
    // oxlint-disable-next-line react/exhaustive-deps
    [
      cell.column.id,
      cell.row.id,
      cell.row.index,
      cell.id,
      cellValue,
      hasRowValuesChanged,
      isSelected,
      sortedRows,
      meta?.name,
      cursor,
      cellMeta,
      handlers,
      showEditableStyles,
      colSpan,
      boxProps,
      pinnedStyles,
      isReorderable,
      isFirstColumn,
      reorderTooltipDisclosure,
      attributes,
      listeners,
      dragHandleStyles,
      t,
    ],
  );
}
